'use client'

import { useEffect } from 'react'
import { reportError } from '@/lib/monitoring'
import './globals.css'

/**
 * The last-resort boundary: a throw in the ROOT layout (the providers, the fonts) takes error.tsx
 * down with it, so this one replaces the whole document and renders its own `<html>` and `<body>`.
 *
 * Deliberately dependency-light: the token stylesheet, so the page still looks like the site, and
 * lib/monitoring.ts, whose only import is Sentry's, loaded on demand — a root-layout failure is the
 * one most worth hearing about. No providers, no next/font, no Base UI, no `<Button>` or `<Link>`:
 * anything else imported here could be the very thing that just threw. For the same reason the way
 * home is a plain `<a>`, a full document load, rather than a client navigation inside the app that
 * just failed to start.
 */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
    useEffect(() => {
        console.error('Root layout error', error)
        reportError(error, { boundary: 'root-layout' })
    }, [error])

    return (
        <html lang="en">
            <body className="min-h-dvh bg-background text-foreground antialiased">
                <main className="grid min-h-dvh place-items-center p-6 text-center">
                    <div className="max-w-sm">
                        <h1 className="text-2xl">Something went wrong</h1>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                            The panel could not start on this page. Reloading usually clears it.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                            <button type="button" onClick={reset} className="inline-flex h-10 cursor-pointer items-center justify-center rounded-lg bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                                Try again
                            </button>
                            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- a full document load is the recovery from a crashed root layout */}
                            <a href="/" className="inline-flex h-10 items-center justify-center rounded-lg border border-border bg-card px-5 text-sm font-medium hover:bg-secondary">
                                Back to the overview
                            </a>
                        </div>
                        {error.digest && <p className="mt-4 font-mono text-xs text-muted-foreground">Reference: {error.digest}</p>}
                    </div>
                </main>
            </body>
        </html>
    )
}
